import React, { useEffect, useState, useRef } from 'react';
import { cx } from 'linaria';
import {
  CurtainBackdrop,
  CurtainContainer,
  CurtainContent,
  CurtainHandle,
  CloseButton,
  HiddenTitle,
  CurtainHeader
} from './Curtain.style';

export const Curtain = ({
  isOpen = false,
  onClose,
  children,
  showHandle = true,
  showCloseButton = true,
  maxHeight = '80vh',
  className,
  title = 'Curtain',
  triggerRef = null
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragDistance, setDragDistance] = useState(0);
  const [hasMounted, setHasMounted] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const curtainRef = useRef(null);

  // Intercept onClose to add animation delay
  const handleClose = () => {
    if (isClosing) return; // Prevent multiple triggers

    setIsClosing(true);
    setIsAnimating(false); // Trigger close animation
    // Don't call onClose() immediately - wait for animation to complete
  };

  // Generate unique ID for ARIA labeling
  const titleId = `curtain-title-${Math.random().toString(36).slice(2, 11)}`;
  const announcementId = `curtain-announcement-${Math.random()
    .toString(36)
    .slice(2, 9)}`;

  // Handle mounting animation and focus management
  useEffect(() => {
    if (isOpen) {
      setHasMounted(true);
      setIsAnimating(false); // Start in closed state
      // Force animation by briefly hiding/showing
      const timer = setTimeout(() => {
        setIsAnimating(true); // Trigger open animation
        setTimeout(() => {
          if (curtainRef.current) {
            curtainRef.current.focus();
          }
        }, 100);
      }, 10);
      return () => clearTimeout(timer);
    }
    // No else block - we handle unmounting in a separate effect
  }, [isOpen]);

  // Prevent body scroll when curtain is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle backdrop click
  const handleBackdropClick = e => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = e => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Drag functionality
  const handleDragStart = e => {
    if (!isOpen) return;

    setIsDragging(true);
    const startY = e.type === 'mousedown' ? e.clientY : e.touches[0].clientY;
    setDragStartY(startY);
    setDragDistance(0);

    // Prevent default to avoid text selection
    e.preventDefault();
  };

  const handleDragMove = e => {
    if (!isDragging || !isOpen) return;

    const currentY = e.type === 'mousemove' ? e.clientY : e.touches[0].clientY;
    const distance = currentY - dragStartY;

    // Only allow downward dragging (positive distance)
    if (distance > 0) {
      setDragDistance(distance);
    }
  };

  const handleDragEnd = () => {
    if (!isDragging) return;

    setIsDragging(false);

    // If dragged more than 100px down, close the curtain
    if (dragDistance > 100) {
      handleClose();
    }

    // Reset drag state
    setDragStartY(0);
    setDragDistance(0);
  };

  // Add global event listeners for drag
  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = e => handleDragMove(e);
      const handleMouseUp = () => handleDragEnd();
      const handleTouchMove = e => handleDragMove(e);
      const handleTouchEnd = () => handleDragEnd();

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, {
        passive: false
      });
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, dragStartY, dragDistance, onClose]);

  return (
    <>
      <CurtainBackdrop
        className={'curtain-backdrop'}
        isOpen={isOpen}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      {hasMounted && (
        <CurtainContainer
          ref={curtainRef}
          isOpen={isAnimating}
          className={cx(className)}
          style={{
            maxHeight,
            transform: isDragging
              ? `translateX(-50%) translateY(${dragDistance}px) scale(${
                  isOpen ? '1' : '0.95'
                })`
              : undefined
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onTransitionEnd={e => {
            if (isClosing && e.propertyName === 'transform') {
              setIsClosing(false);
              setHasMounted(false);
              // Return focus to trigger element when closing
              if (triggerRef && triggerRef.current) {
                triggerRef.current.focus();
              }
              // Now call the actual onClose function
              onClose && onClose();
            }
          }}
        >
          <CurtainHeader>
            {showHandle && (
              <CurtainHandle
                onMouseDown={handleDragStart}
                onTouchStart={handleDragStart}
                onClick={() => {
                  // Only close on click if not dragging
                  if (!isDragging) {
                    handleClose();
                  }
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClose();
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label="Close curtain by dragging down or pressing Enter"
                style={{
                  cursor: isDragging ? 'grabbing' : 'grab',
                  userSelect: 'none'
                }}
              />
            )}
            {showCloseButton && (
              <CloseButton onClick={handleClose} aria-label="Close curtain">
                ×
              </CloseButton>
            )}

            {/* Hidden title for screen readers */}
            <HiddenTitle id={titleId}>{title}</HiddenTitle>
          </CurtainHeader>

          <CurtainContent isOpen={isOpen}>{children}</CurtainContent>
        </CurtainContainer>
      )}

      {/* Screen reader announcements */}
      <div
        id={announcementId}
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      >
        {isOpen && hasMounted ? `${title} opened` : ''}
      </div>
    </>
  );
};
